// Visible-pass prediction.
//
// A pass is "visible" to the naked eye when, at the same moment:
//   1. the satellite is above the observer's horizon (by at least minElevation),
//   2. the observer's sky is dark enough (sun below -6°, civil twilight or darker),
//   3. the satellite itself is in sunlight (not in Earth's shadow).
//
// Strategy: step through the window at a coarse interval to find times the
// satellite is above the horizon, then sample each pass finely to find the
// visible segment and its peak. Only dark-sky time steps are examined, which
// roughly halves the work and skips daytime passes entirely.

import {
  twoline2satrec,
  propagate,
  gstime,
  eciToEcf,
  ecfToLookAngles,
  sunPos,
  shadowFraction,
  jday,
  degreesToRadians,
  type SatRec,
} from "satellite.js";

export interface Satellite {
  norad: number;
  name: string;
  satrec: SatRec;
}

export interface Observer {
  lat: number; // degrees
  lon: number; // degrees
  altM: number; // metres above sea level
}

export interface PassPoint {
  time: string; // ISO 8601 UTC
  azimuth: number; // degrees, 0 = north, clockwise
  elevation: number; // degrees above horizon
  direction: string; // compass point, e.g. "NW"
}

export interface Pass {
  norad: number;
  name: string;
  start: PassPoint; // first visible moment
  peak: PassPoint; // highest visible point
  end: PassPoint; // last visible moment
  durationSec: number;
  // How the visible part ends: fading into Earth's shadow, setting below
  // the horizon/min elevation, or the sky getting too bright.
  endsBy: "shadow" | "horizon" | "twilight";
}

export interface PredictOptions {
  start: Date;
  hours: number;
  minElevation: number; // degrees
  coarseStepSec?: number;
  fineStepSec?: number;
}

const RAD = 180 / Math.PI;
const SUN_DARK_LIMIT = -6; // degrees
const AU_KM = 149597870.7;

const COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];

export function compass(azDeg: number): string {
  return COMPASS[Math.round((((azDeg % 360) + 360) % 360) / 22.5) % 16];
}

/** Parse a CelesTrak-style 3-line TLE text block, optionally only some NORAD ids. */
export function parseTle(text: string, only?: Set<number>): Satellite[] {
  const lines = text.split(/\r?\n/).map((l) => l.trimEnd()).filter((l) => l.length > 0);
  const sats: Satellite[] = [];
  for (let i = 0; i + 2 < lines.length; ) {
    const name = lines[i];
    const l1 = lines[i + 1];
    const l2 = lines[i + 2];
    if (l1.startsWith("1 ") && l2.startsWith("2 ")) {
      if (only && !only.has(parseInt(l1.slice(2, 7), 10))) {
        i += 3;
        continue;
      }
      try {
        const satrec = twoline2satrec(l1, l2);
        if (!satrec.error) {
          sats.push({ norad: parseInt(l1.slice(2, 7), 10), name: name.replace(/^0 /, "").trim(), satrec });
        }
      } catch {
        // skip malformed element sets
      }
      i += 3;
    } else {
      i += 1; // resync on unexpected lines
    }
  }
  return sats;
}

interface Geo {
  latitude: number;
  longitude: number;
  height: number;
}

function toGeo(o: Observer): Geo {
  return {
    latitude: degreesToRadians(o.lat),
    longitude: degreesToRadians(o.lon),
    height: o.altM / 1000,
  };
}

interface SunState {
  rsun: { x: number; y: number; z: number }; // AU, ECI
  elevation: number; // degrees, at observer
}

function sunState(date: Date, geo: Geo): SunState {
  const { rsun } = sunPos(jday(date));
  const gmst = gstime(date);
  const km = { x: rsun.x * AU_KM, y: rsun.y * AU_KM, z: rsun.z * AU_KM };
  const look = ecfToLookAngles(geo as any, eciToEcf(km as any, gmst) as any);
  return { rsun, elevation: look.elevation * RAD };
}

interface SatState {
  azimuth: number;
  elevation: number;
  sunlit: boolean;
}

function satState(sat: Satellite, date: Date, geo: Geo, sun: SunState): SatState | null {
  const pv = propagate(sat.satrec, date);
  if (!pv || !pv.position || typeof pv.position === "boolean") return null;
  const gmst = gstime(date);
  const look = ecfToLookAngles(geo as any, eciToEcf(pv.position, gmst) as any);
  // < 0.5 means more than half the sun's disc is visible from the satellite
  const sunlit = shadowFraction(sun.rsun as any, pv.position) < 0.5;
  return { azimuth: look.azimuth * RAD, elevation: look.elevation * RAD, sunlit };
}

function point(date: Date, s: SatState): PassPoint {
  return {
    time: date.toISOString().replace(/\.\d{3}Z$/, "Z"),
    azimuth: Math.round(s.azimuth),
    elevation: Math.round(s.elevation),
    direction: compass(s.azimuth),
  };
}

export function predictPasses(sats: Satellite[], observer: Observer, opts: PredictOptions): Pass[] {
  const geo = toGeo(observer);
  const coarse = (opts.coarseStepSec ?? 60) * 1000;
  const fine = (opts.fineStepSec ?? 5) * 1000;
  const t0 = Math.floor(opts.start.getTime() / coarse) * coarse;
  const t1 = opts.start.getTime() + opts.hours * 3600_000;

  // The sun moves slowly, so sample it every 10 minutes and interpolate
  // linearly in between (error well under 0.1°). This keeps the fixed cost
  // per request small, which matters on the Workers free plan.
  const SUN_STEP = 600_000;
  const sunBase = Math.floor(t0 / SUN_STEP) * SUN_STEP - SUN_STEP;
  const sunTable: SunState[] = [];
  for (let t = sunBase; t <= t1 + 2 * SUN_STEP; t += SUN_STEP) sunTable.push(sunState(new Date(t), geo));
  const sunAt = (t: number): SunState => {
    const x = (t - sunBase) / SUN_STEP;
    const k = Math.min(Math.max(Math.floor(x), 0), sunTable.length - 2);
    const f = x - k;
    const a = sunTable[k];
    const b = sunTable[k + 1];
    return {
      rsun: {
        x: a.rsun.x + (b.rsun.x - a.rsun.x) * f,
        y: a.rsun.y + (b.rsun.y - a.rsun.y) * f,
        z: a.rsun.z + (b.rsun.z - a.rsun.z) * f,
      },
      elevation: a.elevation + (b.elevation - a.elevation) * f,
    };
  };

  const steps: { t: number; sun: SunState }[] = [];
  for (let t = t0; t <= t1; t += coarse) steps.push({ t, sun: sunAt(t) });
  // A pass may straddle dusk, so look at steps a little either side of the
  // dark window too.
  const darkish = steps.map((s) => s.sun.elevation < SUN_DARK_LIMIT + 1.5);

  const passes: Pass[] = [];

  for (const sat of sats) {
    let i = 0;
    while (i < steps.length) {
      if (!darkish[i]) {
        i++;
        continue;
      }
      const s = satState(sat, new Date(steps[i].t), geo, steps[i].sun);
      if (!s || s.elevation <= 0) {
        i++;
        continue;
      }
      // Satellite is above the horizon at step i. Find the pass extent at the
      // coarse level, then scan it finely from one step before to one after.
      let j = i;
      while (j + 1 < steps.length) {
        const n = satState(sat, new Date(steps[j + 1].t), geo, steps[j + 1].sun);
        if (!n || n.elevation <= 0) break;
        j++;
      }
      const from = Math.max(steps[Math.max(i - 1, 0)].t, opts.start.getTime());
      const to = Math.min(steps[Math.min(j + 1, steps.length - 1)].t, t1);
      const p = scanPass(sat, geo, from, to, fine, opts.minElevation, sunAt);
      if (p) passes.push(p);
      i = j + 2;
    }
  }

  passes.sort((a, b) => a.start.time.localeCompare(b.start.time));
  return passes;
}

/** Sample a satellite's sky track between two times, for drawing the pass. */
export function skyTrack(sat: Satellite, observer: Observer, fromIso: string, toIso: string, samples = 24) {
  const geo = toGeo(observer);
  const a = Date.parse(fromIso);
  const b = Date.parse(toIso);
  const pts: { azimuth: number; elevation: number }[] = [];
  for (let k = 0; k <= samples; k++) {
    const d = new Date(a + ((b - a) * k) / samples);
    const pv = propagate(sat.satrec, d);
    if (!pv || !pv.position || typeof pv.position === "boolean") continue;
    const look = ecfToLookAngles(geo as any, eciToEcf(pv.position, gstime(d)) as any);
    pts.push({ azimuth: look.azimuth * RAD, elevation: look.elevation * RAD });
  }
  return pts;
}

function scanPass(
  sat: Satellite,
  geo: Geo,
  from: number,
  to: number,
  step: number,
  minEl: number,
  sunAt: (t: number) => SunState,
): Pass | null {
  let start: { d: Date; s: SatState } | null = null;
  let peak: { d: Date; s: SatState } | null = null;
  let last: { d: Date; s: SatState } | null = null;
  let endsBy: Pass["endsBy"] = "horizon";

  for (let t = from; t <= to; t += step) {
    const d = new Date(t);
    const sun = sunAt(t);
    const s = satState(sat, d, geo, sun);
    if (!s) continue;
    const dark = sun.elevation < SUN_DARK_LIMIT;
    const visible = dark && s.sunlit && s.elevation >= minEl;
    if (visible) {
      if (!start) start = { d, s };
      if (!peak || s.elevation > peak.s.elevation) peak = { d, s };
      last = { d, s };
    } else if (start) {
      // Visible segment just ended; record why and stop at the first segment.
      endsBy = !s.sunlit ? "shadow" : !dark ? "twilight" : "horizon";
      break;
    }
  }
  if (!start || !peak || !last) return null;
  const durationSec = Math.round((last.d.getTime() - start.d.getTime()) / 1000);
  if (durationSec < 20) return null; // too brief to be worth going outside for
  return {
    norad: sat.norad,
    name: sat.name,
    start: point(start.d, start.s),
    peak: point(peak.d, peak.s),
    end: point(last.d, last.s),
    durationSec,
    endsBy,
  };
}
